import { BadgeCircle, HorizontalMenuTabs } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { CONTENT_GUTTER } from '@backrs_ui/grow-ui/src/components/PageShell/pageShell.layout';
import { type CustomTabId } from '@backrs_ui/grow-ui/src/redux/tabs/customTabIds';
import { type TabItem } from '@backrs_ui/grow-ui/src/redux/tabs/tabKey';

import { Box, SxProps, Theme } from '@mui/material';

import { CUSTOM_TABS } from './customTabs';
import { type BoxType, type InfoBoxConfigMap } from './InfoBox.config';

const STRINGS = appStrings.modules.officeHours.categories;

export type ShellContentTabsProps = Readonly<{
  selectedTab: number;
  onTabChange: (tabIndex: number) => void;
  onCloseTab?: (tab: TabItem) => void;
  tabs: readonly TabItem[];
  infoBoxConfig: InfoBoxConfigMap;
  unreadTabs?: readonly BoxType[];
  tabsWithContent?: readonly BoxType[];
  staticTabs?: readonly CustomTabId[];
  pageTitle?: string;
  // When set, overrides the label of the FEATURED tab.
  featuredTabLabel?: string;
  // Overrides the static label of a custom tab, for tabs named after what they're showing.
  customTabLabels?: Partial<Record<CustomTabId, string>>;
}>;

const NON_CLOSABLE_TABS: ReadonlySet<BoxType> = new Set<BoxType>(['FEATURED']);

const labelForTab = (
  tab: TabItem,
  infoBoxConfig: InfoBoxConfigMap,
  pageTitle?: string,
  featuredTabLabel?: string,
  customTabLabels?: Partial<Record<CustomTabId, string>>,
): string => {
  switch (tab.kind) {
    case 'page-content':
      return pageTitle || STRINGS.current;
    case 'page-tab':
      return tab.pageTab.label;
    case 'resource':
      if (tab.boxType === 'FEATURED' && featuredTabLabel) return featuredTabLabel;

      return infoBoxConfig[tab.boxType]?.title ?? '';
    case 'custom':
      return customTabLabels?.[tab.tabId] ?? CUSTOM_TABS[tab.tabId].title;
  }
};

export const ShellContentTabs = ({
  selectedTab,
  onTabChange,
  onCloseTab,
  tabs,
  infoBoxConfig,
  unreadTabs = [],
  tabsWithContent = [],
  staticTabs = [],
  pageTitle,
  featuredTabLabel,
  customTabLabels,
}: ShellContentTabsProps) => {
  const labels = tabs.map((tab) =>
    labelForTab(tab, infoBoxConfig, pageTitle, featuredTabLabel, customTabLabels),
  );
  const staticTabIds = new Set(staticTabs);

  const tabIndicators = tabs.map((tab) =>
    tab.kind === 'resource' && unreadTabs.includes(tab.boxType) ? (
      <BadgeCircle key={tab.boxType} loading innerOnly size="sm" autoStopMs={2000} />
    ) : null,
  );

  const closableTabs = tabs.map((tab) => {
    if (tab.kind === 'custom') return !staticTabIds.has(tab.tabId);
    if (tab.kind !== 'resource') return false;

    return !NON_CLOSABLE_TABS.has(tab.boxType) && tabsWithContent.includes(tab.boxType);
  });

  const handleCloseTab = onCloseTab
    ? (tabIndex: number) => {
        const tab = tabs[tabIndex];

        if (tab.kind === 'resource' || tab.kind === 'custom') {
          onCloseTab(tab);
        }
      }
    : undefined;

  return (
    <Box sx={containerSx}>
      <HorizontalMenuTabs
        id="shell-content-tabs"
        value={selectedTab}
        setValue={onTabChange}
        tabs={labels}
        variant="minimal-highlighted"
        tabIndicators={tabIndicators}
        closableTabs={closableTabs}
        onCloseTab={handleCloseTab}
      />
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  flexShrink: 0,
  pt: { xs: backrs.spacing['spacing-lg'], md: 0 },
  px: backrs.spacing[CONTENT_GUTTER],
  '& > *:first-of-type': {
    flex: 1,
  },
});
